import type { AgentSessionData } from '@sb/feathers-types';

/**
 * Single row in the chat-history list. The minimum shape the
 * history view + row + delete modal need from the underlying
 * feathers `agent-sessions` record.
 *
 * Real `agent-sessions` rows carry more fields (tokensConsumed,
 * routineSnapshot, compactionState, etc.) — those are out of scope
 * for the history list. The routine-binding hook's
 * `AgentSessionSummary` is an even more minimal shape; the two
 * intentionally differ so binding-only callers don't need to plumb
 * display metadata.
 */
export interface ChatHistorySession {
  id: string;
  /**
   * Routine the session belongs to. Null when the routine has been
   * deleted (FK `ON DELETE SET NULL` per persistence design) — the
   * row is still listed, but Load is disabled.
   */
  routineID: string | null;
  /**
   * Display name of the routine at the time the session was created.
   * Used in the "Routine: <name>" / "Routine deleted: <name>"
   * label even after the routine row itself goes away. Null when
   * the session was started from a non-routine page.
   */
  routineName: string | null;
  /**
   * User-set title, or the auto-generated "Edit routine: <name>" /
   * "Create routine: <name>" the agent stamps when the session binds to
   * the routine it wrote.
   */
  title: string;
  /**
   * Epoch ms of the most recent activity. Drives the relative-time
   * label and the descending sort.
   */
  lastUpdatedAt: number;
}

/**
 * Adapts a feathers `agentSessions` row to the history list's view
 * model. Shared by the live subscription and the rename response so
 * both paths produce identically shaped rows — a rename that mapped
 * fields differently would make the renamed row jump or lose its
 * timestamp label until the next full emission.
 *
 * `routineName` is always null: the row carries `routineID` but not the
 * routine's display name, and the history list does not join against
 * routines.
 */
export function toChatHistorySession(
  row: AgentSessionData,
): ChatHistorySession {
  return {
    id: String(row.id),
    routineID: row.routineID ?? null,
    routineName: null,
    title:
      typeof row.title === 'string' && row.title.length > 0
        ? row.title
        : 'Untitled chat',
    lastUpdatedAt: new Date(
      row.updatedAt ?? row.createdAt ?? Date.now(),
    ).getTime(),
  };
}
